import { Button, type ButtonProps } from '@progress/kendo-react-buttons'
import {
  Drawer,
  DrawerContent,
  DrawerNavigation,
} from '@progress/kendo-react-layout'
import classNames from 'classnames'
import React from 'react'
import { Link } from 'react-router'
import { useToggle } from 'react-use'

import { HStack } from '~/components/elements/HStack'
import { VStack } from '~/components/elements/VStack'
import { useCurrentApplication } from '~/hooks/useCurrentApplication'
import { resolve } from '~/routes/paths'

import { DefaultHeaderContent } from './components/DefaultHeaderContent'
import { DefaultLogo } from './components/DefaultLogo'
import { DefaultNavigationContent } from './components/DefaultNavigationContent'
import { HeaderMenu } from './components/HeaderMenu'
import styles from './DrawerContainer.css'

export type DrawerContainerProps = React.PropsWithChildren<{
  'data-testid'?: string
}>

export const DrawerContainer = ({
  children,
  'data-testid': dataTestId = 'components-features-drawer-container',
}: DrawerContainerProps) => {
  const [isDrawerExpanded, toggleIsDrawerExpanded] = useToggle(true)

  const currentApplication = useCurrentApplication()

  const Logo = currentApplication?.Logo ?? DefaultLogo
  const HeaderContent =
    currentApplication?.HeaderContent ?? DefaultHeaderContent
  const NavigationContent =
    currentApplication?.NavigationContent ?? DefaultNavigationContent
  const drawerToggleButtonIcon: ButtonProps['icon'] = isDrawerExpanded
    ? 'arrow-chevron-left'
    : 'arrow-chevron-right'

  return (
    <Drawer
      className={classNames(
        styles.root,
        isDrawerExpanded && styles.drawerExpanded,
      )}
      data-testid={dataTestId}
      expanded={isDrawerExpanded}
      mini={true}
      miniWidth={64}
      mode="push"
      width={270}
    >
      <DrawerNavigation className={styles.navigation}>
        <VStack className={styles.navigationWrapper} spacing={0}>
          <HStack
            align="center"
            className={classNames(styles.header, styles.navigationHeader)}
            justify="center"
            spacing={0}
          >
            <Link to={currentApplication?.rootPath ?? resolve('top')}>
              <Logo
                expanded={isDrawerExpanded}
                name={currentApplication?.name}
              />
            </Link>
          </HStack>

          <div className={styles.navigationContentContainer}>
            <NavigationContent
              isExpanded={isDrawerExpanded}
              onToggle={toggleIsDrawerExpanded}
            />
          </div>

          <Button
            className={styles.drawerToggleButton}
            fillMode="flat"
            icon={drawerToggleButtonIcon}
            onClick={toggleIsDrawerExpanded}
          />
        </VStack>
      </DrawerNavigation>

      <DrawerContent className={styles.content}>
        <HStack align="center" className={styles.header} spacing={0}>
          <div className={styles.headerContentContainer}>
            <HeaderContent />
          </div>

          <HeaderMenu />
        </HStack>

        <div className={styles.childrenContainer}>{children}</div>
      </DrawerContent>
    </Drawer>
  )
}
